import {
  AsYouType,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  Metadata,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

/**
 * Phone helpers aligned with Weive embed / internationalPhone:
 * national entry, libphonenumber validity, E.164 out.
 */

export const getPhoneExampleForCountry = (country: CountryCode): string => {
  try {
    return getExampleNumber(country, examples)?.formatNational() ?? '';
  } catch {
    return '';
  }
};

/** Longest national digit run the field will accept (includes optional trunk 0). */
export const maxNationalDigitsForCountry = (country: CountryCode): number => {
  try {
    const metadata = new Metadata();
    metadata.selectNumberingPlan(country);
    const lengths = metadata.numberingPlan?.possibleLengths() ?? [];
    if (lengths.length > 0) {
      // +1 so a leading trunk prefix (e.g. Ghana's 0) still fits.
      return Math.max(...lengths) + 1;
    }
  } catch {
    // fall through
  }
  return 15 - String(getCountryCallingCode(country)).length + 1;
};

export const normalizePhoneForCountry = (
  phone: string,
  country: CountryCode,
): string | null => {
  const raw = phone.trim();
  if (!raw) return null;

  try {
    const parsed = parsePhoneNumberFromString(raw, country);
    if (parsed?.isValid()) return parsed.number;
    if (isValidPhoneNumber(raw, country)) {
      return parsePhoneNumberFromString(raw, country)?.number ?? null;
    }
  } catch {
    // fall through
  }
  return null;
};

export const isValidPhoneForCountry = (
  phone: string,
  country: CountryCode,
): boolean => normalizePhoneForCountry(phone, country) != null;

/**
 * Digits → nationally formatted display, capped to the country's possible length.
 * Strips a pasted country calling code so "+233 24…" does not double the dial code.
 */
export const formatNationalPhoneInput = (
  raw: string,
  country: CountryCode,
): string => {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  const dial = String(getCountryCallingCode(country));
  if (digits.startsWith(dial) && digits.length > dial.length + 4) {
    digits = digits.slice(dial.length).replace(/^0+/, '');
  }

  const max = maxNationalDigitsForCountry(country);
  if (digits.length > max) digits = digits.slice(0, max);

  return new AsYouType(country).input(digits) || digits;
};

export const nationalDisplayFromE164 = (e164: string): string => {
  const parsed = parsePhoneNumberFromString(e164);
  return parsed?.formatNational() ?? e164;
};
